import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema.js';

/**
 * De enige weg van de web-app naar de API (ADR-011). `schema.ts` wordt gegenereerd uit
 * docs/api/openapi.json met `pnpm client:generate`; die opdracht hoort bij elke
 * API-wijziging in dezelfde PR.
 */
export interface ApiClientOptions {
  baseUrl: string;
  /** Levert het huidige accesstoken, of null als er geen sessie is. */
  getAccessToken: () => string | null;
  /**
   * Wordt aangeroepen bij een 401. Geeft `true` terug als er zojuist een nieuw
   * accesstoken is opgehaald; het verzoek wordt dan één keer opnieuw gedaan.
   */
  onUnauthorized?: () => Promise<boolean>;
}

/** Wegen waarop een 401 het antwoord zelf is en geen reden om te vernieuwen. */
const AUTH_PATHS = [
  '/auth/login',
  '/platform/auth/login',
  '/auth/refresh',
  '/auth/logout',
  '/auth/password/',
  '/auth/invitations/',
  // Stap 5: activeren en de pincode wijzigen zijn ook aanmeldhandelingen. Een 401 is daar
  // een antwoord ("deze code klopt niet"), geen verlopen sessie om stil te vernieuwen.
  '/auth/activate',
  '/auth/login/pin',
  '/auth/pin',
];

export function createApiClient(options: ApiClientOptions) {
  const client = createClient<paths>({
    baseUrl: options.baseUrl,
    // De refresh-cookie is httpOnly; de browser stuurt hem alleen mee met credentials.
    credentials: 'include',
  });

  // Na fetch(request) is de body van het verzoek verbruikt; voor een herhaling na een 401
  // bewaren we vooraf een kopie. Zonder die kopie gaat een POST/PUT/PATCH zonder body opnieuw
  // uit en krijgt hij een 400 — de offline wachtrij gooit zo'n verzoek dan weg.
  const bodies = new WeakMap<Request, Request>();

  const auth: Middleware = {
    async onRequest({ request }) {
      const token = options.getAccessToken();
      if (token) request.headers.set('authorization', `Bearer ${token}`);
      if (request.method !== 'GET' && request.method !== 'HEAD' && request.body) {
        bodies.set(request, request.clone());
      }
      return request;
    },
    async onResponse({ request, response }) {
      const copy = bodies.get(request);
      bodies.delete(request);
      if (response.status !== 401 || !options.onUnauthorized) return response;

      // Een 401 op een aanmeldweg is een antwoord, geen verlopen sessie: "wachtwoord
      // fout" of "verificatiecode nodig". Opnieuw proberen verandert daar niets aan en
      // vervangt het antwoord door dat van de tweede poging.
      if (AUTH_PATHS.some((path) => request.url.includes(path))) return response;

      // Ook zonder accesstoken proberen we het één keer. Dat is precies het geval na Stap 12: een
      // medewerker die de app offline opende, heeft geen token in het geheugen — de refresh mislukte
      // toen door het ontbrekende netwerk. Zodra er weer bereik is, is het refresh-cookie het enige wat
      // hij nog heeft, en zonder deze poging blijft zijn wachtrij staan met `401` na `401`.
      //
      // Geen lus: `x-up2d8-retry` hieronder laat precies één herhaling toe, en is het cookie ook weg,
      // dan geeft `onUnauthorized` false en blijft de `401` staan.
      // Vernieuw stil en probeer het verzoek precies één keer opnieuw.
      if (request.headers.get('x-up2d8-retry') === '1') return response;
      if (!(await options.onUnauthorized())) return response;

      // GET en HEAD mogen geen body hebben; een Request met body geeft daar een
      // TypeError, en die zou de hele aanroep laten klappen in plaats van te herstellen.
      const hasBody = request.method !== 'GET' && request.method !== 'HEAD';
      const retry = new Request(request.url, {
        method: request.method,
        headers: new Headers(request.headers),
        // Bytes, geen tekst: een multipart-upload moet ongeschonden opnieuw mee.
        body: hasBody && copy ? await copy.arrayBuffer() : undefined,
        credentials: 'include',
      });
      retry.headers.set('x-up2d8-retry', '1');
      const token = options.getAccessToken();
      if (token) retry.headers.set('authorization', `Bearer ${token}`);
      return fetch(retry);
    },
  };

  client.use(auth);
  return client;
}

export type ApiClient = ReturnType<typeof createApiClient>;
export type { paths, components } from './schema.js';

/** Leest de `code` uit een RFC 9457-antwoord, of null als het geen problem+json is. */
export function problemCode(error: unknown): string | null {
  if (error && typeof error === 'object' && 'code' in error && typeof error.code === 'string') {
    return error.code;
  }
  return null;
}

/**
 * De uitleg uit een RFC 9457-antwoord, in de vorm die een gebruiker kan lezen.
 *
 * Eerst de veldfouten uit `errors`, dan `detail`. Die orde is belangrijk: bij een afgewezen
 * webadres staat de bruikbare uitleg ("dit domein staat niet op de lijst") in de veldfout, terwijl
 * `detail` alleen "Controleer de gemarkeerde velden" zegt. Een scherm dat alleen `detail` toont,
 * vertelt de gebruiker dus niets.
 *
 * Een `VALIDATION_FAILED` van het framework zelf (`type: about:blank`, zonder `errors`) geeft
 * `null`: die `detail` is Engelse tekst van NestJS ("Validation failed (uuid is expected)"), niet
 * van ons. Het scherm valt dan terug op zijn eigen Nederlandse tekst (hertest 30 okt.: "Validation
 * failed" in een toast bij een checklist met een lege doelgroep).
 */
export function problemDetail(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;

  const errors = (error as { errors?: unknown }).errors;
  if (Array.isArray(errors)) {
    const messages = errors
      .map((entry) =>
        entry &&
        typeof entry === 'object' &&
        typeof (entry as { message?: unknown }).message === 'string'
          ? (entry as { message: string }).message
          : null,
      )
      .filter((message): message is string => message !== null);
    if (messages.length > 0) return messages.join(' ');
  }

  const { code, type, detail } = error as { code?: unknown; type?: unknown; detail?: unknown };
  if (code === 'VALIDATION_FAILED' && type === 'about:blank') return null;
  return typeof detail === 'string' && detail.length > 0 ? detail : null;
}
